/**
 * Hakkında.
 *
 * Three things in the approved mockup are corrected here, all of them claims
 * about the world that this build cannot make.
 *
 * **"v0.8.1 sürümü hazır" with a "Güncellemeyi Görüntüle" button.** There is no
 * updater, no release feed and nothing to download. The composition is kept;
 * the content says update checking is not available yet.
 *
 * **"doğru, detaylı ve üretime hazır 3D modeller oluşturur".** That is a
 * guarantee of output quality from a system whose only validated 3D output so
 * far is a sparse point cloud, and whose two real captures both failed to
 * reconstruct a head. The description says what SentyCap is for, not what it
 * promises to deliver.
 *
 * **The licence table.** The mockup's COLMAP row literally reads "Lisans
 * bilgisi sürüm envanterinden gösterilir" - placeholder text. The rows here
 * come from the repository's own legal inventory, and a component whose licence
 * has not been verified is not listed at all rather than listed with a guess.
 */

import { useCallback } from "react";
import { getHealth } from "@/api/client";
import { BrandMark } from "@/components/icons";
import { ServerStartedAt } from "@/app/VersionCard";
import { PageHeader, Section } from "@/components/product";
import { Loading } from "@/components/ui";
import { useAsync } from "@/lib/useAsync";
import "./support.css";

/**
 * Verified from `docs/legal/`. Every entry here was checked against the
 * dependency actually installed; nothing is listed on the strength of what a
 * component is usually licensed under.
 */
const LICENCES = [
  { name: "COLMAP", licence: "BSD 3-Clause", use: "Seyrek 3D rekonstrüksiyon motoru" },
  { name: "MediaPipe", licence: "Apache License 2.0", use: "Yüz işaret noktası tespiti" },
  { name: "OpenCV (YuNet)", licence: "Apache License 2.0", use: "Profil yüz tespiti" },
  { name: "FastAPI", licence: "MIT", use: "API sunucusu" },
  { name: "React", licence: "MIT", use: "Arayüz" },
  { name: "qrcode.react", licence: "ISC", use: "Telefon eşleştirme QR kodu" },
];

export function AboutScreen() {
  const fetcher = useCallback((signal: AbortSignal) => getHealth({ signal }), []);
  const { state } = useAsync(fetcher);

  return (
    <div className="stack">
      <PageHeader title="Hakkında" />

      <Section>
        <div className="about__identity">
          <BrandMark size={56} />
          <div>
            <h2 className="about__name">SentyCap</h2>
            <p className="about__version">
              {state.status === "success" ? `Sürüm ${state.data.version}` : <Loading label="Sürüm okunuyor" />}
            </p>
            <p className="muted">Capture Intelligence &amp; 3D Reconstruction</p>
            {/*
              The deployment's own facts, together. This line used to sit at
              the foot of the navigation menu, where it was a sentence about
              process uptime under the links somebody had opened the menu to
              use.
            */}
            <ServerStartedAt />
          </div>
        </div>
      </Section>

      <div className="grid-2">
        <Section title="SentyCap nedir?">
          <p className="muted about__prose">
            SentyCap, çoklu görüntü çekimlerini analiz etmek ve 3D rekonstrüksiyon iş
            akışlarını yönetmek için geliştirilen bir capture-intelligence ve
            rekonstrüksiyon platformudur.
          </p>
          <p className="muted about__prose">
            Çekimlerin hangi bölgeleri ne kadar kapsadığını ölçer, eksik kalan bölgeler
            için ek çekim önerir ve fotogrametri motorunu yönetir. Rekonstrüksiyon
            sonucunun kalitesi büyük ölçüde çekimin kendisine bağlıdır; belirli bir
            doğruluk veya çıktı kalitesi garanti edilmez.
          </p>
        </Section>

        <Section title="Açık kaynak bileşenler">
          <table className="file-table">
            <thead>
              <tr>
                <th>Bileşen</th>
                <th>Lisans</th>
              </tr>
            </thead>
            <tbody>
              {LICENCES.map((item) => (
                <tr key={item.name}>
                  <td>
                    <div className="file-row__name">
                      <div>
                        <p>{item.name}</p>
                        <p className="muted small">{item.use}</p>
                      </div>
                    </div>
                  </td>
                  <td>{item.licence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>

      <Section title="Güncellemeler">
        {/* Layout kept for a future updater; content truthful today. */}
        <p className="muted">
          Güncelleme denetimi bu sürümde kullanılamıyor. SentyCap kendini güncellemez
          ve sürüm sunucusuna bağlanmaz.
        </p>
      </Section>
    </div>
  );
}
